/**
 * Ask Dink starter questions (DNKADM-0460 item 7): the questions an empty panel offers,
 * per screen (four each, except the Transaction ledger's five: Peyton's override, so
 * owners see five there and Staff four). Only three screens have their own set; every
 * other screen shows the Reservations four. Peyton reviews this list in the PR.
 *
 * Each starter names the screen(s) its answer is about (`routes`, id-free paths exactly
 * as the routes list carries them, see `routeCatalogue`). Peyton's rule: a starter shows
 * only when EVERY path it names is in this session's route list (the same list sent to
 * the API), so nobody is offered a question about a screen they cannot open. A starter
 * that names no screen always shows. What is left after filtering is shown as is; a
 * screen never borrows another screen's starters.
 *
 * Rule for adding one: it must be a "how do I / what does this mean" question the help
 * guide answers. Nothing that needs this facility's own settings or bookings (for
 * example "What's our cancellation policy?") until facility answers ship.
 */

/** The screens with their own starters. */
export type StarterScreen = 'reservations' | 'memberDetail' | 'transactionLedger';

export interface AskDinkStarter {
  question: string;
  /** The screens the answer is about; all must be openable for the starter to show. */
  routes: readonly string[];
}

export const ASK_DINK_STARTERS: Readonly<Record<StarterScreen, readonly AskDinkStarter[]>> = {
  reservations: [
    { question: 'How do I move a booking to another bay?', routes: ['/reservations'] },
    { question: 'Why does a booking say Unpaid?', routes: ['/reservations'] },
    { question: 'What happens when I mark a no-show?', routes: ['/reservations'] },
    { question: 'How do I book for a member who called in?', routes: ['/reservations'] },
  ],
  memberDetail: [
    { question: 'How do I give a member free credits?', routes: ['/members'] },
    { question: "How do I change a member's membership?", routes: ['/members'] },
    { question: 'How do I add a card on file for a member?', routes: ['/members'] },
    // DNKADM-0465: replaces "past bookings" (Staff reach bookings from Reservations);
    // member detail's Payments tab is open to Staff now (DNKAPI-0675).
    { question: "Where do I see a member's past payments?", routes: ['/members'] },
  ],
  transactionLedger: [
    // Peyton's replacement for the refund starter (DNKADM-0460): a booking is cancelled,
    // and its money given back, from Reservations.
    {
      question: 'How do I cancel a booking and give the money back?',
      routes: ['/reservations'],
    },
    { question: 'What are upcoming card charges?', routes: ['/transaction-ledger'] },
    // Peyton (DNKADM-0460): answered on member detail's Membership/Credits tab, which
    // Staff can open.
    {
      question: "How do I see a member's credits and when they expire?",
      routes: ['/members'],
    },
    // Peyton (DNKADM-0460): owners and admins only. The renewal date shows on the
    // ledger's Upcoming memberships tab (open to Staff since DNKADM-0465) and the cadence
    // is set on the plan, which stays admin-only, so Staff still do not get this one.
    // The catalogue carries Membership & credits as its child screens (its index only
    // redirects), so the plan screen is the tag.
    {
      question: 'When does a membership renew?',
      routes: ['/transaction-ledger', '/membership-credits/plans'],
    },
    { question: 'Why would a payment fail?', routes: ['/transaction-ledger'] },
  ],
};

/** The set every screen without its own starters falls back to. */
export const DEFAULT_STARTER_SCREEN: StarterScreen = 'reservations';

/** The starters this session may see: every tagged path is in its route list. */
export function visibleStarters(
  starters: readonly AskDinkStarter[],
  allowedPaths: ReadonlySet<string>,
): AskDinkStarter[] {
  return starters.filter((starter) => starter.routes.every((path) => allowedPaths.has(path)));
}
